import type { CaseAgenticMissionLink } from "@/services/api/case-checking";
import type { TaskStep } from "@/services/api/data-hub/types";

import { agenticProgressSubLabel } from "./agenticCaseProgress";
import { deriveAssessmentTaskProgress } from "./assessmentTaskProgress";
import { deriveClassificationTaskProgress } from "./classificationTaskProgress";
import {
  buildMissionStepLabel,
  missionStepPrefix,
  normalizeProgressSubLabel,
} from "./missionStageProgress";
import { deriveTaskStepProgress, formatTaskStepSubLabel } from "./taskStepProgressLabel";

type ItemGroupProgressDeriver = (steps: TaskStep[], overallProgress: number) => { label: string } | null;

/** Item-group-aware labels (stable counts) before generic phase-step derivation. */
const ITEM_GROUP_PROGRESS_DERIVERS: ItemGroupProgressDeriver[] = [
  deriveClassificationTaskProgress,
  deriveAssessmentTaskProgress,
];

const STEP_STATUS_MAP: Record<string, TaskStep["status"]> = {
  completed: "done",
  complete: "done",
  success: "done",
  skipped: "done",
};

export function normalizeTaskStep(step: TaskStep): TaskStep {
  const label = step.label || (step as TaskStep & { name?: string }).name || "";
  const detail = step.detail || (step as TaskStep & { message?: string }).message || "";
  const rawStatus = String(step.status || "pending").toLowerCase();
  const status = STEP_STATUS_MAP[rawStatus] ?? (rawStatus as TaskStep["status"]);
  return {
    ...step,
    label,
    detail,
    status,
    kind: step.kind ?? "step",
  };
}

export function normalizeTaskSteps(steps: TaskStep[]): TaskStep[] {
  return steps.map(normalizeTaskStep);
}

function runningStepDetail(steps: TaskStep[]): string | null {
  const normalized = normalizeTaskSteps(steps);
  const running = normalized.find((step) => step.status === "running" || step.status === "stalled");
  if (running?.detail?.trim()) return running.detail.trim();
  return null;
}

export function buildCaseProgressLabel({
  steps,
  overallProgress,
  statusMessage,
  missionLink,
  isLoadingInitial = false,
}: {
  steps: TaskStep[];
  overallProgress: number;
  statusMessage?: string | null;
  missionLink?: CaseAgenticMissionLink | null;
  isLoadingInitial?: boolean;
}): string | null {
  const prefix = missionStepPrefix(missionLink);

  if (isLoadingInitial) {
    return buildMissionStepLabel(prefix, "Starting…");
  }

  const normalizedSteps = normalizeTaskSteps(steps);
  for (const derive of ITEM_GROUP_PROGRESS_DERIVERS) {
    const itemGroup = derive(normalizedSteps, overallProgress);
    if (itemGroup?.label) {
      return buildMissionStepLabel(prefix, itemGroup.label);
    }
  }

  const phaseProgress = deriveTaskStepProgress(normalizedSteps, overallProgress);
  if (phaseProgress) {
    return buildMissionStepLabel(prefix, formatTaskStepSubLabel(phaseProgress));
  }

  const stepDetail = runningStepDetail(normalizedSteps);
  if (stepDetail) {
    return buildMissionStepLabel(prefix, stepDetail);
  }

  const message = statusMessage?.trim();
  if (message) {
    return buildMissionStepLabel(prefix, normalizeProgressSubLabel(message));
  }

  if (missionLink) {
    return buildMissionStepLabel(prefix, agenticProgressSubLabel(missionLink));
  }

  return buildMissionStepLabel(prefix, "Starting…");
}
